import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import {
  policyPageSchema, breadcrumbSchema, ldScripts,
} from "@/lib/seo/schema";

const TITLE = "Privacy Policy - Indonesian Calendar";
const DESC = "Indonesian Calendar privacy policy: what data we collect, cookies, analytics, advertising, and your rights.";

export const Route = createFileRoute("/en/privacy-policy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Privacy Policy" },
      { property: "og:description", content: "How Indonesian Calendar handles your data and privacy." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/en/privacy-policy/" }],
    scripts: ldScripts([
      policyPageSchema({ url: "/en/privacy-policy", name: TITLE, description: DESC, kind: "PrivacyPolicy", locale: "en", dateModified: "2026-05-01" }),
      breadcrumbSchema([
        { name: "Home", url: "/en" },
        { name: "Privacy Policy", url: "/en/privacy-policy" },
      ]),
    ]),
  }),
  component: PrivacyPageEn,
});

const TOC = [
  { id: "data", label: "Data we collect" },
  { id: "cookies", label: "Cookies & analytics" },
  { id: "ads", label: "Advertising" },
  { id: "storage", label: "Data storage" },
  { id: "rights", label: "Your rights" },
  { id: "changes", label: "Policy changes" },
];

function PrivacyPageEn() {
  return (
    <SiteShell locale="en">
      <InfoPageLayout
        variant="legal"
        title="Privacy Policy"
        subtitle="Transparency about data, cookies, and ads."
        badge="P"
        lastUpdated="May 1, 2026"
        locale="en"
        breadcrumbs={[{ label: "Home", href: "/en" }, { label: "Privacy Policy" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">Contents</h3>
            <ol className="space-y-1.5 text-muted-foreground">
              {TOC.map((t, i) => (
                <li key={t.id}>
                  <a className="hover:text-primary" href={`#${t.id}`}>{i + 1}. {t.label}</a>
                </li>
              ))}
            </ol>
          </div>
        }
      >
        <p>
          Indonesian Calendar ("we") respects user privacy. This page explains
          what data we collect when you use this site and how it is used.
        </p>

        <h2 id="data">1. Data we collect</h2>
        <ul>
          <li><strong>Technical data</strong>: IP address, browser, device, pages visited, visit time.</li>
          <li><strong>Preferences</strong>: theme (light/dark) and language are stored in your browser's <code>localStorage</code> - never sent to our server.</li>
          <li>We do <strong>not</strong> ask for name, email, or any personal identification to use the site.</li>
        </ul>

        <h2 id="cookies">2. Cookies &amp; analytics</h2>
        <p>
          We may use third-party analytics (e.g. Google Analytics, Plausible) to
          understand aggregate usage patterns. You can block cookies via your
          browser settings.
        </p>

        <h2 id="ads">3. Advertising</h2>
        <p>
          Some pages may display ads from partners (e.g. Google AdSense). Ad
          partners may use cookies for personalization. Please review each
          partner's privacy policy.
        </p>

        <h2 id="storage">4. Data storage</h2>
        <p>
          Public holiday, joint leave, and calendar conversion data are public
          and not linked to any individual user.
        </p>

        <h2 id="rights">5. Your rights</h2>
        <p>
          You may request deletion of analytics data related to your visits by
          contacting us via the <Link to={"/en/contact" as never}>Contact</Link> page.
        </p>

        <h2 id="changes">6. Policy changes</h2>
        <p>
          This policy may be updated at any time. The update date appears at
          the top of this page.
        </p>
      </InfoPageLayout>
    </SiteShell>
  );
}